/**
 * 🏠 Inicio — dashboard hero con KPIs, accesos rápidos y resumen visual.
 * Reloj y datos se actualizan EN DIRECTO.
 */
import { h, apiClient, formatEur, pill, statTile, btn } from './_common.js';

// Timers globales para que se limpien al cambiar de sección
let _clockInterval = null;
let _dataInterval = null;
let _visibilityListener = null;

export async function renderInicio(root, { user } = {}) {
  root.innerHTML = '';
  // Limpiar timers anteriores
  if (_clockInterval) { clearInterval(_clockInterval); _clockInterval = null; }
  if (_dataInterval) { clearInterval(_dataInterval); _dataInterval = null; }
  if (_visibilityListener) { document.removeEventListener('visibilitychange', _visibilityListener); _visibilityListener = null; }

  const u = user || { fullName: 'Carlos', nickname: 'Carlos' };
  const greet = greeting();
  const wrapper = h('div', { class: 'animate-fade-in' });

  // Hero
  wrapper.appendChild(h('section', { class: 'mb-8 bg-gradient-to-br from-gold-500/10 via-base-900 to-purple-500/5 border border-gold-400/20 rounded-2xl p-6 md:p-8' }, [
    h('div', { class: 'flex items-start justify-between flex-wrap gap-3' }, [
      h('div', {}, [
        h('p', { class: 'text-sm text-gold-400 font-medium tracking-widest uppercase' }, greet),
        h('h2', { class: 'font-display text-3xl md:text-5xl font-bold mt-2' }, `Hola, ${u.nickname || u.fullName?.split(' ')[0] || 'Carlos'}.`),
        h('p', { class: 'mt-3 text-ink-300 max-w-2xl text-sm md:text-base' }, 'Tu sistema personal está activo y monitorizando todos tus frentes.'),
      ]),
      h('div', { class: 'flex flex-col items-end' }, [
        h('div', { id: 'live-date', class: 'text-xs text-ink-400' }, new Date().toLocaleDateString('es-ES', { weekday: 'long', day: '2-digit', month: 'long' })),
        h('div', { class: 'flex items-center gap-2' }, [
          h('span', { id: 'live-indicator', class: 'inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse', title: 'En directo' }),
          h('div', { id: 'live-clock', class: 'text-2xl font-display font-bold text-gold-300 tabular-nums' }, new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' })),
        ]),
      ]),
    ]),
    // Quick actions
    h('div', { class: 'flex flex-wrap gap-2 mt-5' }, [
      quickAction('✅', 'Nueva tarea', () => location.hash = '#tareas'),
      quickAction('📝', 'Nueva nota', () => location.hash = '#notas'),
      quickAction('🏠', 'Buscar oportunidades', async () => {
        const { toast } = await import('./_common.js');
        const t = toast('🔎 Buscando oportunidades inmobiliarias…');
        try {
          const r = await apiClient.post('/api/v1/real-estate/scout/run', {});
          t.update(r.message || `${r.opportunitiesNew} oportunidades nuevas, ${r.hotDeals} hot deals`, r.opportunitiesNew > 0 ? 'success' : 'info');
          setTimeout(() => { location.hash = '#inmuebles'; }, 1200);
        } catch (e) {
          t.update('Error al buscar: ' + (e?.message || 'desconocido'), 'error');
        }
      }),
      quickAction('💰', 'Ver patrimonio', () => location.hash = '#patrimonio'),
      quickAction('🚗', 'Autosell', () => location.hash = '#autosell'),
    ]),
  ]));

  // KPIs grid
  wrapper.appendChild(h('div', { id: 'kpi-grid', class: 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6' }, [
    h('div', { class: 'col-span-full md:col-span-3 lg:col-span-6 text-center text-ink-400 text-sm py-4' }, 'Cargando KPIs...'),
  ]));

  // Section cards
  wrapper.appendChild(h('div', { id: 'sections-grid', class: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' }));

  root.appendChild(wrapper);
  renderHoyCenter(wrapper);

  // Carga datos en paralelo
  const [tasks, properties, vehicles, notes, goals, portfolio, oppStats] = await Promise.all([
    apiClient.get('/api/v1/tasks?limit=5').catch(() => ({ data: [], total: 0 })),
    apiClient.get('/api/v1/properties?limit=20').catch(() => ({ data: [], total: 0 })),
    apiClient.get('/api/v1/vehicles?limit=20').catch(() => ({ data: [], total: 0 })),
    apiClient.get('/api/v1/notes?limit=5').catch(() => ({ data: [], total: 0 })),
    apiClient.get('/api/v1/goals?limit=3').catch(() => ({ data: [], total: 0 })),
    apiClient.get('/api/v1/patrimonio/unified').catch(() => null),
    apiClient.get('/api/v1/real-estate/stats').catch(() => ({ totals: {}, bySource: {} })),
  ]);

  // KPIs + section cards
  refreshKpis(wrapper, { tasks, properties, vehicles, notes, portfolio, oppStats, goals });

  // ============= RELOJ EN DIRECTO (cada segundo) =============
  _clockInterval = setInterval(() => {
    const clockEl = document.getElementById('live-clock');
    const dateEl = document.getElementById('live-date');
    if (!clockEl) {
      // Si el elemento ya no existe (cambio de sección), parar
      clearInterval(_clockInterval); _clockInterval = null;
      return;
    }
    const now = new Date();
    clockEl.textContent = now.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    if (dateEl) dateEl.textContent = now.toLocaleDateString('es-ES', { weekday: 'long', day: '2-digit', month: 'long' });
  }, 1000);

  // ============= AUTO-REFRESH DATOS (cada 60s + on focus) =============
  const refreshData = async () => {
    if (document.hidden) return; // no refrescar si pestaña oculta
    try {
      const [tasks2, properties2, vehicles2, notes2, goals2, portfolio2, oppStats2] = await Promise.all([
        apiClient.get('/api/v1/tasks?limit=5').catch(() => tasks),
        apiClient.get('/api/v1/properties?limit=20').catch(() => properties),
        apiClient.get('/api/v1/vehicles?limit=20').catch(() => vehicles),
        apiClient.get('/api/v1/notes?limit=5').catch(() => notes),
        apiClient.get('/api/v1/goals?limit=3').catch(() => goals),
        apiClient.get('/api/v1/portfolio/latest').catch(() => portfolio),
        apiClient.get('/api/v1/real-estate/stats').catch(() => oppStats),
      ]);
      if (!document.getElementById('kpi-grid')) {
        clearInterval(_dataInterval); _dataInterval = null;
        return;
      }
      refreshKpis(wrapper, { tasks: tasks2, properties: properties2, vehicles: vehicles2, notes: notes2, goals: goals2, portfolio: portfolio2, oppStats: oppStats2 });
    } catch { /* silent */ }
  };
  _dataInterval = setInterval(refreshData, 60000); // cada 1 min

  // Refresh inmediato al volver de otra pestaña
  _visibilityListener = () => { if (!document.hidden) refreshData(); };
  document.addEventListener('visibilitychange', _visibilityListener);
}

function refreshKpis(wrapper, { tasks, properties, vehicles, notes, portfolio, oppStats, goals = { data: [], total: 0 } }) {
  const kpiGrid = wrapper.querySelector('#kpi-grid');
  if (!kpiGrid) return;
  kpiGrid.innerHTML = '';
  kpiGrid.appendChild(statTile({ label: 'Tareas', value: tasks.total || 0, emoji: '✅', color: 'gold' }));
  kpiGrid.appendChild(statTile({ label: 'Inmuebles', value: properties.total || 0, emoji: '🏠', color: 'blue' }));
  kpiGrid.appendChild(statTile({ label: 'Vehículos', value: vehicles.total || 0, emoji: '🚗', color: 'red' }));
  kpiGrid.appendChild(statTile({ label: 'Patrimonio', value: portfolio?.totals?.netWorth != null ? formatEur(portfolio.totals.netWorth) : '—', emoji: '💰', color: 'green' }));
  kpiGrid.appendChild(statTile({ label: 'Oportunidades', value: oppStats?.totals?.total || 0, emoji: '💎', color: 'purple' }));
  kpiGrid.appendChild(statTile({ label: 'Notas', value: notes.total || 0, emoji: '📝', color: 'gold' }));

  // Section cards (cada una clicable) — clear and rebuild
  const grid = wrapper.querySelector('#sections-grid');
  if (!grid) return;
  grid.innerHTML = '';
  grid.appendChild(sectionCard({
    emoji: '✅', title: 'Tareas activas', subtitle: `${tasks.total || 0} en total`, hash: 'tareas', color: 'gold',
    items: (tasks.data || []).slice(0, 5).map((t) => ({ text: t.title, sub: t.section || '' })),
    emptyText: 'Sin tareas pendientes — ¡todo al día!',
  }));
  grid.appendChild(sectionCard({
    emoji: '🏠', title: 'Inmuebles', subtitle: `${properties.total || 0} en cartera`, hash: 'inmuebles', color: 'blue',
    items: (properties.data || []).slice(0, 5).map((p) => ({ text: p.name, sub: p.city || p.operationMode || '' })),
    emptyText: 'Sin inmuebles. Añade el primero desde Inmuebles.',
    badge: oppStats?.totals?.hotDeals ? `🔥 ${oppStats.totals.hotDeals} hot deals` : null,
  }));
  grid.appendChild(sectionCard({
    emoji: '🚗', title: 'Autosell pipeline', subtitle: `${vehicles.total || 0} vehículos`, hash: 'autosell', color: 'red',
    items: (vehicles.data || []).slice(0, 5).map((v) => ({ text: `${v.brand} ${v.model}`, sub: v.status })),
    emptyText: 'Pipeline vacío.',
  }));
  grid.appendChild(sectionCard({
    emoji: '📝', title: 'Notas recientes', subtitle: `${notes.total || 0} en total`, hash: 'notas', color: 'purple',
    items: (notes.data || []).slice(0, 5).map((n) => ({ text: n.title || '(sin título)', sub: n.scope || '' })),
    emptyText: 'Sin notas todavía.',
  }));
  grid.appendChild(sectionCard({
    emoji: '🎯', title: 'Objetivos', subtitle: `${goals.total || 0} activos`, hash: 'patrimonio', color: 'green',
    items: (goals.data || []).slice(0, 5).map((g) => ({ text: g.title, sub: g.targetAmount ? formatEur(g.targetAmount) : '' })),
    emptyText: 'Define objetivos en Patrimonio.',
  }));
  grid.appendChild(h('div', { class: 'bg-base-800/40 border border-base-700 rounded-xl p-5 hover:border-emerald-400/30 cursor-pointer transition group', onclick: () => location.hash = '#sistema' }, [
    h('div', { class: 'flex items-start justify-between mb-3' }, [
      h('div', { class: 'flex items-center gap-2' }, [
        h('span', { class: 'text-2xl' }, '⚙️'),
        h('h3', { class: 'font-display font-semibold' }, 'Sistema'),
      ]),
      h('span', { class: 'text-xs text-ink-400 group-hover:text-gold-300 transition' }, '→'),
    ]),
    h('div', { class: 'space-y-1.5 text-sm' }, [
      h('div', { class: 'flex justify-between' }, [h('span', { class: 'text-ink-300' }, 'API'), pill('online', 'green')]),
      h('div', { class: 'flex justify-between' }, [h('span', { class: 'text-ink-300' }, 'IA Router'), pill('activo', 'green')]),
      h('div', { class: 'flex justify-between' }, [h('span', { class: 'text-ink-300' }, 'Scouts'), pill('cron 6h', 'gold')]),
      h('div', { class: 'flex justify-between' }, [h('span', { class: 'text-ink-300' }, 'Backups'), pill('Hetzner', 'green')]),
    ]),
  ]));
}

function quickAction(emoji, label, onclick) {
  return h('button', {
    class: 'flex items-center gap-2 bg-base-800/60 hover:bg-base-700/80 border border-base-700 hover:border-gold-400/50 px-3 py-2 rounded-lg text-sm transition',
    onclick,
  }, [
    h('span', { class: 'text-lg' }, emoji),
    h('span', {}, label),
  ]);
}

function sectionCard({ emoji, title, subtitle, hash, color, items, emptyText, badge }) {
  const colorClasses = {
    gold: 'hover:border-gold-400/40', blue: 'hover:border-sky-400/40',
    red: 'hover:border-red-400/40', green: 'hover:border-emerald-400/40',
    purple: 'hover:border-purple-400/40',
  };
  return h('div', {
    class: `bg-base-800/40 border border-base-700 ${colorClasses[color] || colorClasses.gold} rounded-xl p-5 cursor-pointer transition group`,
    onclick: () => location.hash = '#' + hash,
  }, [
    h('div', { class: 'flex items-start justify-between mb-3' }, [
      h('div', { class: 'flex items-center gap-2' }, [
        h('span', { class: 'text-2xl' }, emoji),
        h('div', {}, [
          h('h3', { class: 'font-display font-semibold' }, title),
          h('p', { class: 'text-xs text-ink-400' }, subtitle),
        ]),
      ]),
      h('span', { class: 'text-xs text-ink-400 group-hover:text-gold-300 transition' }, '→'),
    ]),
    badge ? h('div', { class: 'inline-flex items-center text-xs bg-red-500/20 text-red-300 border border-red-400/30 px-2 py-0.5 rounded-full mb-2' }, badge) : null,
    items.length === 0
      ? h('p', { class: 'text-sm text-ink-400 italic' }, emptyText)
      : h('div', { class: 'space-y-1.5' }, items.map((it) => h('div', { class: 'flex items-center justify-between text-sm py-1 border-b border-base-700/30 last:border-0' }, [
          h('span', { class: 'text-ink-200 truncate flex-1' }, it.text),
          it.sub ? h('span', { class: 'text-xs text-ink-400 ml-2 shrink-0' }, it.sub) : null,
        ]))),
  ]);
}

function greeting() {
  const hh = new Date().getHours();
  if (hh < 6) return 'Madrugada';
  if (hh < 12) return 'Buenos días';
  if (hh < 20) return 'Buenas tardes';
  return 'Buenas noches';
}

async function renderHoyCenter(container) {
  try {
    const r = await apiClient.get('/api/v1/hoy').catch(() => null);
    if (!r || !r.items) return;
    const card = h('div', { class: 'mb-6 bg-base-800/60 border border-gold-400/30 rounded-2xl p-5' }, [
      h('div', { class: 'flex items-center justify-between mb-3' }, [
        h('div', { class: 'flex items-center gap-2' }, [
          h('span', { class: 'text-2xl' }, '🎯'),
          h('h3', { class: 'font-display text-xl font-bold' }, 'Centro HOY'),
        ]),
        h('span', { class: 'text-xs text-ink-300' }, 'Actualizado hace ' + Math.floor((Date.now() - r.generated_at)/1000) + 's'),
      ]),
      h('div', { class: 'space-y-2' }, r.items.map(it => h('div', {
        class: 'flex items-start gap-3 p-3 rounded-xl ' + (
          it.priority === 'critical' ? 'bg-rose-500/15 border border-rose-400/40' :
          it.priority === 'high' ? 'bg-orange-500/15 border border-orange-400/40' :
          it.priority === 'info' ? 'bg-blue-500/10 border border-blue-400/30' :
          'bg-emerald-500/10 border border-emerald-400/30'
        )
      }, [
        h('span', { class: 'text-2xl shrink-0' }, it.emoji),
        h('div', { class: 'flex-1 min-w-0' }, [
          h('div', { class: 'font-semibold text-ink-100 text-sm' }, it.title),
          h('p', { class: 'text-xs text-ink-300 mt-0.5' }, it.desc),
        ]),
        it.action_label ? h('a', { href: it.action_url, target: '_blank', class: 'bg-gold-500/20 hover:bg-gold-500/30 text-gold-200 px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap' }, it.action_label) : null,
      ].filter(Boolean)))),
    ]);
    container.prepend(card);
  } catch(e) {}
}
